import React, {FC, useCallback, useEffect, useMemo, useState} from "react";
import moment from "moment";
import enUS from "date-fns/locale/en-US";
import {useDispatch} from "react-redux";
import {
    Box,
    ChakraProps,
    Flex,
    Grid,
    HStack,
    Input,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Text,
    useBreakpointValue,
} from "@chakra-ui/react";
import type {ReactNode} from "react";
import {Breadcrumbs} from "../../components/Breadcrumbs/Breadcrumbs";
import {DatePickerUpdateStyled} from "../../components/DatePicker/DatePicker";
import {DownloadButton} from "../../components/Buttons/DownloadButton";
import {Loader} from "../../components/Loader/Loader";
import {Paginator} from "../../components/Paignator/Paginator";
import {setBreadcrumbsLinks} from "../../redux/Breadcrumbs/BreadcrumbsReducer";
import {BreadcrumbsData} from "../../shared/entities/Breadcrumbs/BreadCrumbsData";
import {ProductSalesController} from "../../controllers/ProductSalesController";
import {
    ProductSalesItem,
    ProductSalesPeriodMode,
    ProductSalesReport as ProductSalesReportData,
    ProductSalesSortBy,
} from "../../shared/entities/Order/ProductSales";
import {hasPermission} from "../../features/Permission/AllowedAccess";
import {productSalesViewPermission} from "../permissions";
import {
    buildMonthAnchor,
    dateStringToDate,
    dateToDateString,
    dateToWeekAnchor,
    defaultProductSalesPeriodAnchor,
    ENGLISH_MONTHS,
    formatProductSalesPeriodLabel,
    parseMonthAnchor,
    resolveProductSalesPeriod,
    weekAnchorToDate,
} from "../../shared/utils/productSalesPeriod";
import {DownloaderInstance} from "../../service/Downloader/Downloder";
import {AiOutlineFileExcel} from "react-icons/ai";
import ImageCustom from "../../components/Image/ImageCustom";

const GRID_TEMPLATE = "56px minmax(220px, 2fr) 90px 120px 120px 140px";
const PAGE_SIZE = 20;

const filterControlProps = {
    h: "32px",
    minH: "32px",
    size: "sm" as const,
    borderRadius: "md",
    fontSize: "13px",
    lineHeight: "1.25rem",
    borderColor: "gray.200",
    bg: "white",
    _hover: {borderColor: "gray.300"},
    _focusVisible: {
        borderColor: "blue.400",
        boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
    },
};

const dateFilterWrapperProps: ChakraProps = {
    h: "32px",
    minH: "32px",
    minW: 0,
    w: "100%",
    maxW: {base: "100%", md: "170px"},
    ml: 0,
    display: "flex",
    alignItems: "center",
    px: 2,
    py: 0,
    fontSize: "13px",
    lineHeight: "1.25rem",
    color: "gray.800",
    bg: "white",
    borderColor: "gray.200",
    borderWidth: "1px",
    borderRadius: "md",
    _hover: {
        borderColor: "gray.300",
    },
    _focusWithin: {
        borderColor: "blue.400",
        boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
    },
    sx: {
        "& .react-datepicker__input-container > input": {
            fontSize: "13px",
        },
        "& .react-datepicker__input-container > input::placeholder": {
            color: "var(--chakra-colors-gray-500)",
        },
    },
};

const PERIOD_OPTIONS: {value: ProductSalesPeriodMode; label: string}[] = [
    {value: "week", label: "Week"},
    {value: "month", label: "Month"},
    {value: "year", label: "Year"},
    {value: "custom", label: "Custom"},
];

const SORT_OPTIONS: {value: ProductSalesSortBy; label: string}[] = [
    {value: "quantitySold", label: "Qty sold"},
    {value: "orderCount", label: "Orders"},
    {value: "totalAmount", label: "Revenue"},
    {value: "productName", label: "Product name"},
];

const GRID_CELL_SX = {
    "& > *": {
        px: 2,
        display: "flex",
        alignItems: "center",
        minWidth: 0,
        overflow: "hidden",
    },
    "& > *:not(:last-child)": {
        borderRight: "1px solid",
        borderColor: "gray.100",
    },
};

const ProductSalesGridRow = ({
    cells,
    variant = "body",
}: {
    cells: ReactNode[];
    variant?: "header" | "body";
}) => {
    const isHeader = variant === "header";
    return (
        <Grid
            templateColumns={GRID_TEMPLATE}
            w="100%"
            alignItems="center"
            columnGap={0}
            px={2}
            py={isHeader ? 2 : 0}
            minH={isHeader ? "35px" : "56px"}
            fontSize="13px"
            fontWeight={isHeader ? 700 : undefined}
            bg={isHeader ? "aliceblue" : "white"}
            borderBottom="1px solid #71809640"
            sx={{
                ...GRID_CELL_SX,
                ...(isHeader
                    ? {"& > *:not(:last-child)": {borderRight: "1px solid", borderColor: "gray.200"}}
                    : {}),
            }}
        >
            {cells.map((cell, idx) => (
                <Box
                    key={idx}
                    justifyContent={idx === cells.length - 1 ? "flex-end" : "flex-start"}
                >
                    {cell}
                </Box>
            ))}
        </Grid>
    );
};

const formatMoney = (value: number) => `$${Number(value || 0).toFixed(2)}`;

const resolveProductPrice = (item: ProductSalesItem) =>
    Number(item.productPrice ?? item.product?.price ?? 0);

const productImageCell = (item: ProductSalesItem) => (
    item.product ? (
        <ImageCustom
            elem={item.product}
            sizeInPercent={100}
            currentSize="small"
            sizeBackend="COMMON"
        />
    ) : (
        <Box w="48px" h="48px" bg="gray.100" borderRadius="sm"/>
    )
);

const ProductSalesReport: FC<{links: Array<BreadcrumbsData>}> = ({links}) => {
    const dispatch = useDispatch();
    const isMobile = useBreakpointValue({base: true, lg: false});
    const controller = useMemo(() => new ProductSalesController(dispatch), [dispatch]);

    const [periodMode, setPeriodMode] = useState<ProductSalesPeriodMode>("month");
    const [periodAnchor, setPeriodAnchor] = useState(() => defaultProductSalesPeriodAnchor("month"));
    const [customFrom, setCustomFrom] = useState(() => moment().startOf("month").format("YYYY-MM-DD"));
    const [customTo, setCustomTo] = useState(() => moment().format("YYYY-MM-DD"));
    const [productName, setProductName] = useState("");
    const [debouncedProductName, setDebouncedProductName] = useState("");
    const [sortBy, setSortBy] = useState<ProductSalesSortBy>("quantitySold");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [report, setReport] = useState<ProductSalesReportData>({
        items: [],
        count: 0,
        totalQuantitySold: 0,
        totalDistinctOrders: 0,
        totalAmount: 0,
    });

    setBreadcrumbsLinks(dispatch, links);

    const periodRange = useMemo(
        () => resolveProductSalesPeriod(periodMode, periodAnchor, customFrom, customTo),
        [periodMode, periodAnchor, customFrom, customTo],
    );

    useEffect(() => {
        const timerId = window.setTimeout(() => setDebouncedProductName(productName.trim()), 300);
        return () => window.clearTimeout(timerId);
    }, [productName]);

    const loadReport = useCallback(() => {
        if (!hasPermission(productSalesViewPermission)) {
            return;
        }
        setIsLoading(true);
        controller
            .report({
                from: periodRange.from,
                to: periodRange.to,
                productName: debouncedProductName || undefined,
                sortBy,
                sortDir,
                page: currentPage - 1,
                size: PAGE_SIZE,
            })
            .then((res) => {
                if (res) {
                    setReport(res);
                }
            })
            .finally(() => setIsLoading(false));
    }, [controller, periodRange.from, periodRange.to, debouncedProductName, sortBy, sortDir, currentPage]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    const handlePeriodModeChange = (nextMode: string) => {
        const mode = nextMode as ProductSalesPeriodMode;
        setPeriodMode(mode);
        setPeriodAnchor(defaultProductSalesPeriodAnchor(mode));
        setCurrentPage(1);
    };

    const renderPeriodAnchorControl = () => {
        if (periodMode === "week") {
            return (
                <DatePickerUpdateStyled
                    wrapperProps={dateFilterWrapperProps}
                    placeholderText="Week of"
                    selected={weekAnchorToDate(periodAnchor)}
                    locale={enUS}
                    dateFormat="MM-dd-yyyy"
                    isClearable={false}
                    onChange={(date) => {
                        setPeriodAnchor(dateToWeekAnchor(date));
                        setCurrentPage(1);
                    }}
                />
            );
        }
        if (periodMode === "month") {
            const {year, month} = parseMonthAnchor(periodAnchor);
            return (
                <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={isMobile ? 3 : 2}
                    align={isMobile ? "stretch" : "center"}
                    w="100%"
                >
                    <Select
                        {...filterControlProps}
                        title="Month"
                        aria-label="Month"
                        w={isMobile ? "100%" : "150px"}
                        ml={0}
                        value={month}
                        onChange={(e) => {
                            setPeriodAnchor(buildMonthAnchor(year, e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        {ENGLISH_MONTHS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </Select>
                    <Select
                        {...filterControlProps}
                        title="Year"
                        aria-label="Year"
                        w={isMobile ? "100%" : "100px"}
                        ml={0}
                        value={year}
                        onChange={(e) => {
                            setPeriodAnchor(buildMonthAnchor(e.target.value, month));
                            setCurrentPage(1);
                        }}
                    >
                        {yearOptions.map((yearOption) => (
                            <option key={yearOption} value={String(yearOption)}>{yearOption}</option>
                        ))}
                    </Select>
                </Stack>
            );
        }
        if (periodMode === "year") {
            return (
                <Select
                    {...filterControlProps}
                    title="Year"
                    aria-label="Year"
                    w={isMobile ? "100%" : "120px"}
                    ml={0}
                    value={periodAnchor}
                    onChange={(e) => {
                        setPeriodAnchor(e.target.value);
                        setCurrentPage(1);
                    }}
                >
                    {yearOptions.map((year) => (
                        <option key={year} value={String(year)}>{year}</option>
                    ))}
                </Select>
            );
        }
        if (periodMode === "custom") {
            return (
                <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={isMobile ? 3 : 2}
                    align={isMobile ? "stretch" : "center"}
                    w="100%"
                >
                    <DatePickerUpdateStyled
                        wrapperProps={dateFilterWrapperProps}
                        placeholderText="From"
                        selected={dateStringToDate(customFrom)}
                        locale={enUS}
                        dateFormat="MM-dd-yyyy"
                        isClearable
                        onChange={(date) => {
                            setCustomFrom(dateToDateString(date));
                            setCurrentPage(1);
                        }}
                    />
                    {!isMobile && (
                        <Text fontSize="13px" color="gray.500" flexShrink={0}>to</Text>
                    )}
                    <DatePickerUpdateStyled
                        wrapperProps={dateFilterWrapperProps}
                        placeholderText="To"
                        selected={dateStringToDate(customTo)}
                        locale={enUS}
                        dateFormat="MM-dd-yyyy"
                        isClearable
                        onChange={(date) => {
                            setCustomTo(dateToDateString(date));
                            setCurrentPage(1);
                        }}
                    />
                </Stack>
            );
        }
        return null;
    };

    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const years: number[] = [];
        for (let year = currentYear; year >= currentYear - 10; year -= 1) {
            years.push(year);
        }
        return years;
    }, []);

    const desktopRow = (item: ProductSalesItem) => (
        <ProductSalesGridRow
            key={item.productId}
            cells={[
                productImageCell(item),
                <Text key="name" noOfLines={2}>{item.product?.name || item.productName}</Text>,
                <Text key="price">{formatMoney(resolveProductPrice(item))}</Text>,
                <Text key="qty">{item.quantitySold}</Text>,
                <Text key="orders">{item.orderCount}</Text>,
                <Text key="amount" fontWeight="medium">{formatMoney(item.totalAmount)}</Text>,
            ]}
        />
    );

    const mobileRow = (item: ProductSalesItem) => (
        <Box key={item.productId} border="1px solid #71809640" borderRadius="md" p={3} mb={2} bg="white">
            <Flex align="center" mb={2}>
                <Box flexShrink={0} w="56px" mr={3}>
                    {productImageCell(item)}
                </Box>
                <Text fontWeight="semibold" fontSize="13px" flex={1}>{item.product?.name || item.productName}</Text>
            </Flex>
            <Flex justify="space-between" fontSize="12px" mb={1}>
                <Text color="gray.600">Price</Text>
                <Text>{formatMoney(resolveProductPrice(item))}</Text>
            </Flex>
            <Flex justify="space-between" fontSize="12px" mb={1}>
                <Text color="gray.600">Qty sold</Text>
                <Text>{item.quantitySold}</Text>
            </Flex>
            <Flex justify="space-between" fontSize="12px" mb={1}>
                <Text color="gray.600">Orders</Text>
                <Text>{item.orderCount}</Text>
            </Flex>
            <Flex justify="space-between" fontSize="12px">
                <Text color="gray.600">Revenue</Text>
                <Text fontWeight="medium">{formatMoney(item.totalAmount)}</Text>
            </Flex>
        </Box>
    );

    const periodRadioGroup = (
        <RadioGroup value={periodMode} onChange={handlePeriodModeChange}>
            {isMobile ? (
                <Stack spacing={2} align="stretch" w="100%">
                    {PERIOD_OPTIONS.map((option) => (
                        <Radio key={option.value} value={option.value} size="sm" w="100%" ml={0}>
                            <Text fontSize="12px" lineHeight="1.2">
                                {option.label}
                            </Text>
                        </Radio>
                    ))}
                </Stack>
            ) : (
                <HStack spacing={4} flexWrap="wrap" align="center">
                    {PERIOD_OPTIONS.map((option) => (
                        <Radio key={option.value} value={option.value} size="sm">
                            <Text fontSize="13px" lineHeight="1.2" whiteSpace="nowrap">
                                {option.label}
                            </Text>
                        </Radio>
                    ))}
                </HStack>
            )}
        </RadioGroup>
    );

    const summaryLine = (
        <Text fontSize={isMobile ? "12px" : "13px"} color="gray.700" lineHeight="tall">
            Period:{" "}
            <Text as="span" fontWeight="semibold" color="blue.600">
                {formatProductSalesPeriodLabel(periodMode, periodAnchor, customFrom, customTo)}
            </Text>
            {" · "}
            Products:{" "}
            <Text as="span" fontWeight="semibold">{report.count}</Text>
            {" · "}
            Qty sold:{" "}
            <Text as="span" fontWeight="semibold">{report.totalQuantitySold}</Text>
            {" · "}
            Orders:{" "}
            <Text as="span" fontWeight="semibold">{report.totalDistinctOrders}</Text>
            {" · "}
            Revenue:{" "}
            <Text as="span" fontWeight="semibold">{formatMoney(report.totalAmount)}</Text>
        </Text>
    );

    const excelExportButton = (
        <DownloadButton
            clickAction={() => DownloaderInstance.getProductSalesXls({
                from: periodRange.from,
                to: periodRange.to,
                productName: debouncedProductName || undefined,
                sortBy,
                sortDir,
            })}
            icon={AiOutlineFileExcel}
            label="Export Excel"
            nameFile="product-sales.xlsx"
        />
    );

    const productFiltersStack = (
        <Stack
            direction={isMobile ? "column" : "row"}
            spacing={isMobile ? 3 : 2}
            align={isMobile ? "stretch" : "center"}
            ml={isMobile ? 0 : "auto"}
            w={isMobile ? "100%" : undefined}
        >
            <Input
                {...filterControlProps}
                placeholder="Product name"
                title="Filter by product name"
                w={isMobile ? "100%" : "220px"}
                minW={0}
                ml={0}
                value={productName}
                onChange={(e) => {
                    setProductName(e.target.value);
                    setCurrentPage(1);
                }}
            />
            <Select
                {...filterControlProps}
                title="Sort by"
                aria-label="Sort by"
                w={isMobile ? "100%" : "150px"}
                flexShrink={0}
                ml={0}
                value={sortBy}
                onChange={(e) => {
                    setSortBy(e.target.value as ProductSalesSortBy);
                    setCurrentPage(1);
                }}
            >
                {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </Select>
            <Select
                {...filterControlProps}
                title="Sort direction"
                aria-label="Sort direction"
                w={isMobile ? "100%" : "110px"}
                flexShrink={0}
                ml={0}
                value={sortDir}
                onChange={(e) => {
                    setSortDir(e.target.value as "asc" | "desc");
                    setCurrentPage(1);
                }}
            >
                <option value="desc">Desc</option>
                <option value="asc">Asc</option>
            </Select>
            {!isMobile && (
                <Flex alignSelf={undefined} ml={0} pl={0}>
                    {excelExportButton}
                </Flex>
            )}
        </Stack>
    );

    const mobileSummaryRow = (
        <Flex align="flex-start" gap={2} w="100%">
            <Box flex={1} minW={0}>
                {summaryLine}
            </Box>
            <Flex flexShrink={0} alignItems="center" h="32px">
                {excelExportButton}
            </Flex>
        </Flex>
    );

    if (!hasPermission(productSalesViewPermission)) {
        return (
            <>
                <Breadcrumbs/>
                <Text mt={4} fontSize="13px">You do not have permission to view this page.</Text>
            </>
        );
    }

    return (
        <>
            <Breadcrumbs/>
            <Flex flexDir="column" height="100%" fontFamily="Poppins">
                <Box
                    mb={3}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor="gray.200"
                    bg="white"
                    px={{base: 3, md: 4}}
                    py={4}
                    boxShadow="0 1px 2px rgba(0, 0, 0, 0.04)"
                >
                    {isMobile ? (
                        <Stack spacing={3} align="stretch" w="100%">
                            {periodRadioGroup}
                            <Box w="100%" ml={0}>
                                {renderPeriodAnchorControl()}
                            </Box>
                            {productFiltersStack}
                            {mobileSummaryRow}
                        </Stack>
                    ) : (
                        <Flex
                            flexDir="row"
                            justifyContent="space-between"
                            alignItems="end"
                            flexWrap="nowrap"
                            gap={3}
                        >
                            <Stack spacing={4} align="stretch" flex={1} minW={0} w="100%">
                                {periodRadioGroup}
                                <Box w="100%" ml={0}>
                                    {renderPeriodAnchorControl()}
                                </Box>
                                {summaryLine}
                            </Stack>
                            {productFiltersStack}
                        </Flex>
                    )}
                </Box>

                {isLoading ? (
                    <Loader/>
                ) : (
                    <>
                        {!isMobile && (
                            <Box border="1px solid #71809640" borderRadius="md" overflow="hidden">
                                <ProductSalesGridRow
                                    variant="header"
                                    cells={[
                                        <Text key="h-img"/>,
                                        <Text key="h-name">Product</Text>,
                                        <Text key="h-price">Price</Text>,
                                        <Text key="h-qty">Qty sold</Text>,
                                        <Text key="h-orders">Orders</Text>,
                                        <Text key="h-amount">Revenue</Text>,
                                    ]}
                                />
                                {report.items.length === 0 ? (
                                    <Box p={4}>
                                        <Text fontSize="13px" color="gray.600">No products sold in the selected period.</Text>
                                    </Box>
                                ) : (
                                    report.items.map(desktopRow)
                                )}
                            </Box>
                        )}

                        {isMobile && (
                            <Box>
                                {report.items.length === 0 ? (
                                    <Text fontSize="13px" color="gray.600">No products sold in the selected period.</Text>
                                ) : (
                                    report.items.map(mobileRow)
                                )}
                            </Box>
                        )}

                        {report.count > PAGE_SIZE && (
                            <Box mt={4}>
                                <Paginator
                                    limit={report.count}
                                    step={PAGE_SIZE}
                                    current={currentPage}
                                    swapPageTrigger={setCurrentPage}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Flex>
        </>
    );
};

export default ProductSalesReport;
